/**
 * Profile translation utilities for the Store Profile Page.
 *
 * These helpers mirror the transformListingForLanguage pattern used on the
 * server side (server/events/listing.js) and apply locale-specific translations
 * that were stored in user publicData by the translation cron.
 *
 * Translation storage format in publicData:
 *   {
 *     storeName: "My Store",          // owner's original language
 *     storeAbout: "About text",
 *     fr: { storeName: "Mon magasin", storeAbout: "...", storeFaqs: [...] },
 *     es: { storeName: "Mi tienda",   storeAbout: "...", storeFaqs: [...] },
 *     zh: { storeName: "我的店铺",    storeAbout: "...", storeFaqs: [...] },
 *   }
 */

// Simple publicData fields translated by the microservice
const PROFILE_SIMPLE_FIELDS = [
  'storeName',
  'storeAbout',
  'seoTitle',
  'seoDescription',
  'storeVideoTitle',
  'storeVideoDescription',
];

/**
 * Apply locale-specific translations to a Sharetribe user object.
 *
 * Returns the user unchanged when:
 *  - The requested locale matches the store's defaultLanguage
 *  - No translation data exists for the requested locale
 *
 * @param {Object} userProfile  - Sharetribe user entity
 * @param {string} locale       - Visitor's current locale (e.g. 'fr', 'es', 'zh')
 * @returns {Object} User with publicData fields translated (deep-clone of relevant parts)
 */
export const transformProfileForLanguage = (userProfile, locale) => {
  if (!userProfile || !locale) return userProfile;

  const profileAttrs = userProfile?.attributes?.profile || {};
  const publicData = profileAttrs.publicData || {};
  const defaultLanguage = publicData.defaultLanguage || 'en';

  // No transform needed: visitor's locale is the store's default language
  if (locale === defaultLanguage) return userProfile;

  const langData =
    publicData[locale] ||
    publicData?.translations?.[locale];
  if (!langData) return userProfile; // Translation not yet available — show original

  const mergedPublicData = { ...publicData };

  // Apply simple field translations
  PROFILE_SIMPLE_FIELDS.forEach(field => {
    if (langData[field] != null) {
      mergedPublicData[field] = langData[field];
    }
  });

  // Apply FAQ translations (entire array is replaced when translated)
  if (Array.isArray(langData.storeFaqs)) {
    mergedPublicData.storeFaqs = langData.storeFaqs;
  }

  // Apply exception name translations (only the `name` field on each exception)
  if (Array.isArray(mergedPublicData.exceptions) && Array.isArray(langData.exceptions)) {
    mergedPublicData.exceptions = mergedPublicData.exceptions.map((ex, i) => ({
      ...ex,
      name: langData.exceptions[i]?.name ?? ex.name,
    }));
  }

  // Optional: profile bio translated under locale bucket (if provided by translation pipeline)
  const mergedProfile = { ...profileAttrs, publicData: mergedPublicData };
  if (langData.bio != null && String(langData.bio).trim() !== '') {
    mergedProfile.bio = langData.bio;
  }

  return {
    ...userProfile,
    attributes: {
      ...userProfile.attributes,
      profile: mergedProfile,
    },
  };
};

/**
 * Get the translated display name for a store category.
 * Falls back through: locale name → English name → base name field.
 *
 * @param {Object} category  - Store category with optional `names` map
 * @param {string} locale    - Target locale
 * @returns {string}
 */
export const getCategoryName = (category, locale) => {
  if (!category) return '';
  if (!locale || locale === 'en') {
    return category.names?.en || category.name || '';
  }
  return category.names?.[locale] || category.names?.en || category.name || '';
};
